import { localsync } from 'localsync';
import { authenticatedSelector } from 'AppSrc/core/auth/auth-selectors';
import {
  checkTokenValid,
  logout,
  setAuthorizationFromJWTToken,
} from 'AppSrc/core/auth/auth-actions';
import uniqid from 'uniqid';
import {
  axiosInstance,
} from 'AppSrc/core/queries/axios-queries';
import {
  SET_JWT_TOKEN_IN_LOCAL_SYNC_CONTROLLER,
} from './redux-local-synch-controller-actions';
import {
  REDUX_INIT,
} from './utility-actions';

// create a uniqid representing this global context (essentially the browser tab)
// we need to keep track of this as it seems that localsync has a bug in safari where
// the subscriber is notified even if the trigger event was in the same tab
const thisTabId = uniqid();
/*
  Create a synchronizer channel.
  `localsync` supports N number of synchronizers for different things across your app.
  The key 'user' defines a localsync synchronization channel.
*/
const createTokenSync = localsync({ channel: 'token' });


// We set this as null as we need it to exist in outer scope. However we also need to
// wait for redux to initiate before we can define it.
let tokenLocalSyncController = null;



/** Create a subscriber that will receive published payloads on all tabs that did not trigger the sync. */
const createSubscriber = (dispatch, getState) => (current, previous, url) => {
  // on safari, trigger is being called within the same tab, which messes everything up.
  // make sure this doesn't happen by nooping if the passed in tabId is equal to thisTabId
  const sameTab = current && (current.tabId === thisTabId);
  if (sameTab) return;

  const authenticated = authenticatedSelector(getState());
  console.log('TOKEN SUBSCRIBER? previous, current, authenticated?: ', previous, current, authenticated);
  // if (!authenticated) {
  //   dispatch(checkTokenValid(StaticToken));
  //   // dispatch(checkTokenValid());
  // }
  // Case 1: Initial load and NO token is present in localsync from last time
  // (current.token === null && previous.token === undefined)
  // DO NOTHING!!!! Let the login screen do SAMLAuthenticate
  // as only the login screen itself knows which route it was redirected from to me the relayState
  // Case 2: Initial load and a token is present in localsync from last time
  if ((current !== null) && (previous !== null) && current.token && previous.token === undefined) {
    console.log('INITIAL LOAD: TOKEN PRESENT FROM LAST SESSION !!! ', current.token);
    // check wether a token on the request header is valid by making getV1Authcheck request with no data
    dispatch(checkTokenValid(current.token));
  }
  // Case 3: token has been set in another tab
  else if ((current !== null) && (previous !== null) && current.token && previous.token === null) {
    console.log('TOKEN SET IN ANOTHER TAB !!! ', previous.token, current.token);
    // We don't need to check authorisation as this was done just now in the other tab
    // instead we set the token directly
    // DVB: if the checkTokenValid failed, this was causing a loop between two open tabs of each
    // tab logging out and then logging in the other
    dispatch(setAuthorizationFromJWTToken(current.token));
  }
  // Case 4: token has been cleared in another tab
  // MM: there is a weird issue when the previous token is no longer there there but logout does not happen
  // I think it happens when you clear the token in a tab where is was not originally set,
  // then it clears is a strange way in the original tab that set the token
  // checking for authenticated as well as previous token solves the problem
  else if ((current !== null) && current.token === null && (((previous !== null) && previous.token) || authenticated)) {
    console.log('TOKEN CLEARED IN ANOTHER TAB !!! ', (previous && previous.token), current.token);
    dispatch(logout());
  }
  // Case 5: Token updated (behind the scene, without visible login)
  // TODO, NOT SUPPORTED YET BY API !!!
  /* else if (previous.token && current.token) {
    console.log('TOKEN UPDATED !!! ', previous.token, current.token);
  } */
};




const reduxLocalSyncController = ({
  dispatch,
  getState,
}) => {
  /* Create a publisher that optionally transforms some data to a payload to be transmitted to other tabs. */
  const publisher = (token, tabId) => ({
    token,
    tabId,
  });

  const subscriber = createSubscriber(dispatch, getState);

  // Create a controller for the synchronizer.
  tokenLocalSyncController = createTokenSync(publisher, subscriber);

  return (next) => (action) => {
    const defaultReturnValue = next(action);

    const latestToken = axiosInstance.defaults.headers.common.Authorization;

    // we start the tokenLocalSynchController after redux store initiates
    if (action.type === REDUX_INIT) {
      /* ------------------------------------------- */
      /*   MM: third party localsync module does not allow you to choose when the INITIAL read of a possible token from last session in localstorage happens
      /*   it just happens when localsync object is created
      /*   Therefore the only way to get it to work is to create it just after store is init
      /*   So it can dispatch a store action if a token is found
      /* ------------------------------------------- */
      /*
        Start synchronizing.
        Passing true tells localsync to poll the current storage mechanism once on
        start for any pre-existing state that may be there (cross session).
        Defaults to false - may change to true in a future major version.
      */
      tokenLocalSyncController.start(true);
      // it's important we return here as opposed to triggering further actions
      return defaultReturnValue;
    }


    if (action.type === SET_JWT_TOKEN_IN_LOCAL_SYNC_CONTROLLER) {
      const {
        JWTToken,
      } = action.payload;

      tokenLocalSyncController.trigger(JWTToken, thisTabId);

      // it's important we return here as opposed to triggering further actions
      return defaultReturnValue;
    }

    // On every action (with the exception of the above actions), we check the current token
    // in axios and compare it to the one in redux. If the token has changed, we update the redux
    // token accordingly
    if (latestToken) {
      const token = getState().auth.token;
      if (token !== latestToken) {
        dispatch(setAuthorizationFromJWTToken(latestToken));
        // it's important we return here as opposed to triggering further actions
        return defaultReturnValue;
      }
    }

    return defaultReturnValue;
  };
};


export {
  reduxLocalSyncController,
};

